import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Col, Row } from '@/lib/utils';
import { DemiLightText, MediumText, RegularText } from '@/lib/Text';
import SvgIcon from '@/components/SvgIcon';
import { colors } from '@/lib/colors';
import { cloneDeep } from 'lodash';
import {
    useCenterSessionCommissionTables,
    usePutCenterSessionCommissionTables,
} from '@/features/centerSessionCommissionTable/hooks';
import {
    CoachSessionBrackets,
    CoachSessionBracketsBody,
    SessionBracket,
    SessionCommissionType,
} from '@/features/centerSessionCommissionTable/types';
import { BracketInput, RemoveButton, CoachListPanel } from './BracketPrimitives';
import { ToggleSwitch } from '../ModalCommission';
import { TooltipWrapper } from '@/components/TooltipWrapper';
import ExceptionRatesSummary from './exceptionRates/ExceptionRatesSummary';
import { useExceptionRatesEditor } from './exceptionRates/useExceptionRatesEditor';

export interface SessionTierCommissionTabRef {
    handleApply: () => Promise<void>;
    handleApplyToAll: () => void;
    validate: () => { valid: boolean; message?: string };
}

type Props = {
    centerId?: string;
    year: string;
    month: string;
    onIsModifiedChange?: (isModified: boolean) => void;
};

type LocalCoach = CoachSessionBrackets;

const defaultBracket = (minSessions: number = 1): SessionBracket => ({
    minSessions,
    maxSessions: null,
    commissionRate: 0,
});

const SessionTierCommissionTab = forwardRef<SessionTierCommissionTabRef, Props>(
    ({ centerId, year, month, onIsModifiedChange }, ref) => {
        const { data: sessionData, isLoading } = useCenterSessionCommissionTables({
            centerId: centerId || '',
            year,
            month,
            enabled: !!centerId,
        });

        const putMutation = usePutCenterSessionCommissionTables();
        // 예외 수업료 — 새 서버(exceptionRatesAllModes)일 때만 보인다. 회차 구간표와 따로 강사 1명씩 저장한다.
        const exceptionEditor = useExceptionRatesEditor({ centerId, year, month, settlementMode: 'sessionTier' });

        const [localData, setLocalData] = useState<LocalCoach[]>([]);
        const [initialData, setInitialData] = useState<LocalCoach[]>([]);
        const [selectedCoachId, setSelectedCoachId] = useState<string | null>(null);
        // 센터 전체 공통 정산 방식 (정률제/정액제)
        const [commissionType, setCommissionType] = useState<SessionCommissionType>('percentage');
        const [initialCommissionType, setInitialCommissionType] = useState<SessionCommissionType>('percentage');
        // 타입 전환 시 값 보존을 위한 타입별 스냅샷 (%↔원 왕복해도 각 타입 입력값 유지)
        const [savedByType, setSavedByType] = useState<{
            percentage: LocalCoach[] | null;
            fixed: LocalCoach[] | null;
        }>({ percentage: null, fixed: null });

        useEffect(() => {
            if (sessionData?.coachSessionCommissionTables) {
                const initialValue: LocalCoach[] = sessionData.coachSessionCommissionTables.map((coach) => ({
                    coachId: coach.coachId,
                    coachName: coach.coachName,
                    scheduleBrackets:
                        coach.scheduleBrackets?.length > 0 ? cloneDeep(coach.scheduleBrackets) : [defaultBracket()],
                }));
                setLocalData(initialValue);
                setInitialData(cloneDeep(initialValue));

                const initialType: SessionCommissionType = sessionData.commissionType ?? 'percentage';
                setCommissionType(initialType);
                setInitialCommissionType(initialType);
                setSavedByType({ percentage: null, fixed: null });

                setSelectedCoachId((prev) => {
                    if (!prev && initialValue.length > 0) {
                        return initialValue[0].coachId;
                    }
                    return prev;
                });
            }
        }, [sessionData]);

        const isCoachModified = useCallback(
            (id: string) => {
                const current = localData.find((c) => c.coachId === id);
                const initial = initialData.find((c) => c.coachId === id);
                if (!current || !initial) return false;

                const stripId = (b: any) => {
                    const { _id, ...rest } = b;
                    return rest;
                };

                return (
                    JSON.stringify(current.scheduleBrackets.map(stripId)) !==
                    JSON.stringify(initial.scheduleBrackets.map(stripId))
                );
            },
            [localData, initialData],
        );

        useEffect(() => {
            if (!onIsModifiedChange) return;
            const anyBracketModified = localData.some((c) => isCoachModified(c.coachId));
            const typeModified = commissionType !== initialCommissionType;
            onIsModifiedChange(anyBracketModified || typeModified);
        }, [localData, commissionType, initialCommissionType, isCoachModified, onIsModifiedChange]);

        const validateCoach = useCallback(
            (coach: LocalCoach): { valid: boolean; message?: string } => {
                const brackets = coach.scheduleBrackets;
                if (brackets.length === 0) {
                    return { valid: false, message: `${coach.coachName} 강사의 구간을 1개 이상 설정해주세요.` };
                }
                for (let i = 0; i < brackets.length; i++) {
                    const b = brackets[i];
                    if (b.minSessions === null || b.minSessions === undefined || b.minSessions < 1) {
                        return { valid: false, message: `${coach.coachName} 강사의 시작 회차는 1 이상이어야 합니다.` };
                    }
                    if (b.commissionRate === null || b.commissionRate === undefined) {
                        return {
                            valid: false,
                            message:
                                commissionType === 'percentage'
                                    ? `${coach.coachName} 강사의 수수료율을 입력해주세요.`
                                    : `${coach.coachName} 강사의 회당 정산액을 입력해주세요.`,
                        };
                    }
                    if (b.commissionRate < 0) {
                        return {
                            valid: false,
                            message:
                                commissionType === 'percentage'
                                    ? `${coach.coachName} 강사의 수수료율은 0 이상이어야 합니다.`
                                    : `${coach.coachName} 강사의 회당 정산액은 0 이상이어야 합니다.`,
                        };
                    }
                    if (commissionType === 'percentage' && b.commissionRate > 100) {
                        return { valid: false, message: `${coach.coachName} 강사의 수수료율은 0~100 사이여야 합니다.` };
                    }
                    if (b.maxSessions !== null && b.maxSessions !== undefined) {
                        if (b.maxSessions < b.minSessions) {
                            return {
                                valid: false,
                                message: `${coach.coachName} 강사의 구간 ${i + 1}에서 종료 회차는 시작 회차 이상이어야 합니다.`,
                            };
                        }
                    }
                    // 마지막이 아닌데 maxSessions가 null이면 안됨
                    if (i < brackets.length - 1 && (b.maxSessions === null || b.maxSessions === undefined)) {
                        return {
                            valid: false,
                            message: `${coach.coachName} 강사의 구간 ${i + 1}에 종료 회차를 입력해주세요. (마지막 구간만 무제한 가능)`,
                        };
                    }
                    // 연속성 검증
                    if (i > 0) {
                        const prev = brackets[i - 1];
                        if (prev.maxSessions !== null && prev.maxSessions !== undefined) {
                            if (prev.maxSessions + 1 !== b.minSessions) {
                                return {
                                    valid: false,
                                    message: `${coach.coachName} 강사의 구간이 연속되지 않습니다. (이전 종료 회차 + 1 = 다음 시작 회차)`,
                                };
                            }
                        }
                    }
                }
                return { valid: true };
            },
            [commissionType],
        );

        const validateAll = useCallback((): { valid: boolean; message?: string } => {
            for (const coach of localData) {
                const res = validateCoach(coach);
                if (!res.valid) return res;
            }
            return { valid: true };
        }, [localData, validateCoach]);

        useImperativeHandle(ref, () => ({
            validate: validateAll,
            handleApply: async () => {
                if (!centerId) return;
                const bodyCoaches: CoachSessionBracketsBody[] = localData.map((c) => ({
                    coachId: c.coachId,
                    scheduleBrackets: c.scheduleBrackets.map((b) => {
                        const { _id: _omit, ...rest } = b as any;
                        return rest as SessionBracket;
                    }),
                }));
                await putMutation.mutateAsync({
                    centerId,
                    body: {
                        year,
                        month,
                        commissionType,
                        coachSessionCommissionTables: bodyCoaches,
                    },
                });
            },
            handleApplyToAll: () => {
                if (!selectedCoachId) return;
                const selected = localData.find((c) => c.coachId === selectedCoachId);
                if (!selected) return;
                setLocalData((prev) =>
                    prev.map((coach) => ({
                        ...coach,
                        scheduleBrackets: cloneDeep(selected.scheduleBrackets),
                    })),
                );
            },
        }));

        const handleAddBracket = () => {
            setLocalData((prev) => {
                const next = cloneDeep(prev);
                const idx = next.findIndex((c) => c.coachId === selectedCoachId);
                if (idx === -1) return prev;
                const arr = next[idx].scheduleBrackets;
                const last = arr[arr.length - 1];
                const newMin =
                    last && last.maxSessions !== null && last.maxSessions !== undefined ? last.maxSessions + 1 : 1;
                arr.push({ minSessions: newMin, maxSessions: null, commissionRate: 0 });
                return next;
            });
        };

        const handleRemoveBracket = (index: number) => {
            setLocalData((prev) => {
                const next = cloneDeep(prev);
                const idx = next.findIndex((c) => c.coachId === selectedCoachId);
                if (idx === -1) return prev;
                const arr = next[idx].scheduleBrackets;
                arr.splice(index, 1);
                if (arr.length === 0) {
                    arr.push(defaultBracket());
                }
                return next;
            });
        };

        const handleChangeBracket = (index: number, field: keyof SessionBracket, value: number | null) => {
            setLocalData((prev) => {
                const next = cloneDeep(prev);
                const idx = next.findIndex((c) => c.coachId === selectedCoachId);
                if (idx === -1) return prev;
                const arr = next[idx].scheduleBrackets;
                arr[index][field] = value as never;
                return next;
            });
        };

        // 정률제/정액제 전환 (전체 공통)
        // 현재 타입 입력값을 스냅샷으로 저장하고, 대상 타입의 저장값이 있으면 복원 / 없으면 정산액 0으로 시작
        const handleToggleCommissionType = (value: SessionCommissionType) => {
            if (value === commissionType) return;

            // 현재 타입의 입력값 보존
            setSavedByType((prev) => ({ ...prev, [commissionType]: cloneDeep(localData) }));

            const restored = savedByType[value];
            if (restored) {
                setLocalData(cloneDeep(restored));
            } else {
                // 대상 타입 첫 진입: 구간 구조는 유지하되 정산액만 0으로 초기화
                setLocalData((prev) => {
                    const next = cloneDeep(prev);
                    next.forEach((coach) => {
                        coach.scheduleBrackets.forEach((b) => {
                            b.commissionRate = 0;
                        });
                    });
                    return next;
                });
            }

            setCommissionType(value);
        };

        const selectedCoach = localData.find((c) => c.coachId === selectedCoachId);

        const rateUnit = commissionType === 'percentage' ? '%' : '원';
        const rateLabel = commissionType === 'percentage' ? '적용 요율' : '회당 정산액';

        if (isLoading) return <div className="p-4">Loading...</div>;

        return (
            <Row className="flex flex-row w-full mt-[10px]">
                {/* 왼쪽 강사 리스트 */}
                <CoachListPanel
                    coaches={localData.map((c) => ({ coachId: c.coachId, coachName: c.coachName }))}
                    selectedCoachId={selectedCoachId}
                    onSelect={setSelectedCoachId}
                    isCoachModified={isCoachModified}
                />

                {/* 구분선 */}
                <div className="w-[0.5px] bg-[#E6E8EA] mx-[20px]" />

                {/* 오른쪽 컨텐츠 */}
                {selectedCoach && (
                    <Col className="flex flex-col overflow-y-scroll pr-[16px]">
                        {/* 정률제/정액제 선택 (센터 전체 공통) */}
                        <Row className="items-center mb-[24px] gap-[12px]">
                            <MediumText fontSize={16} color="#3D4244">
                                정률제/정액제 선택
                            </MediumText>
                            <ToggleSwitch value={commissionType} onToggle={handleToggleCommissionType} />
                        </Row>

                        {/* 안내 배너 (hover 시 상세 예시 툴팁) */}
                        <TooltipWrapper
                            className="w-full mb-[20px]"
                            tooltip={{
                                title: '각 수업이 해당 강사와 회원 사이에서 총 누적 몇 번째 수업인지 확인해 정산 구간을 적용합니다. 예: 1~50회 3만원, 51~100회 5만원으로 설정한 경우, 같은 달에 50회차와 51회차 수업이 모두 있으면 50회차 수업은 3만원, 51회차 수업은 5만원으로 각각 정산됩니다.',
                                width: 360,
                                lineHeight: 20,
                            }}
                        >
                            <Row className="items-center w-full px-[16px] py-[12px] bg-[#EEF3FF] rounded-[10px] gap-[8px] cursor-help">
                                <SvgIcon name="SvgInfoInCircle" size={17} fill={colors.primary} />
                                <RegularText fontSize={14} lineHeight={20} color="#1D4ED8">
                                    해당 강사-회원의 수업별 누적 회차에 따라 정산 구간이 자동 적용됩니다.
                                </RegularText>
                            </Row>
                        </TooltipWrapper>

                        <Col className="mb-[40px]">
                            <Row className="items-center mb-[20px] gap-[8px]">
                                <MediumText fontSize={18} color="#3D4244">
                                    {commissionType === 'percentage'
                                        ? '세션 구간별 수수료율'
                                        : '세션 구간별 회당 정산액'}
                                </MediumText>
                            </Row>

                            <Row className="mb-[12px]">
                                <div className="w-[244px]">
                                    <DemiLightText fontSize={14} color="#6B7280">
                                        회차 구간 (이상~이하)
                                    </DemiLightText>
                                </div>
                                <div className="w-[150px]" />
                                <div className="w-[110px]">
                                    <DemiLightText fontSize={14} color="#6B7280">
                                        {rateLabel}
                                    </DemiLightText>
                                </div>
                            </Row>

                            <Col className="gap-[16px]">
                                {selectedCoach.scheduleBrackets.map((bracket, index) => (
                                    <Row key={index} className="items-center">
                                        <BracketInput
                                            value={bracket.minSessions}
                                            onChange={(val) => handleChangeBracket(index, 'minSessions', val)}
                                            unit="회"
                                            width={178}
                                        />
                                        <RegularText className="mx-[12px] text-[#6B7280]">~</RegularText>
                                        <BracketInput
                                            value={bracket.maxSessions}
                                            onChange={(val) => handleChangeBracket(index, 'maxSessions', val)}
                                            unit="회"
                                            placeholder="무제한"
                                            width={178}
                                        />

                                        <div className="w-[12px]" />

                                        <BracketInput
                                            value={bracket.commissionRate}
                                            onChange={(val) => {
                                                if (val !== null) {
                                                    if (commissionType === 'percentage' && val > 100) return;
                                                    if (commissionType === 'fixed' && String(val).length > 7) return;
                                                }
                                                handleChangeBracket(index, 'commissionRate', val);
                                            }}
                                            unit={rateUnit}
                                            width={178}
                                        />

                                        <RemoveButton onClick={() => handleRemoveBracket(index)} />
                                    </Row>
                                ))}
                            </Col>

                            <Row className="mt-[16px] items-center cursor-pointer gap-[8px]" onClick={handleAddBracket}>
                                <SvgIcon name="SvgPlus" size={16} fill={colors.primary} />
                                <RegularText fontSize={16} color={colors.primary}>
                                    추가하기
                                </RegularText>
                            </Row>
                        </Col>

                        {/* 예외 수업료 — 구간표 다음에 읽히게 맨 아래(상세 정산 탭과 같은 자리) */}
                        {exceptionEditor.visible && selectedCoachId && exceptionEditor.hasCoach(selectedCoachId) && (
                            <ExceptionRatesSummary
                                rules={exceptionEditor.rulesOf(selectedCoachId)}
                                readOnly={exceptionEditor.readOnly}
                                categoryName={exceptionEditor.categoryName}
                                settlementMode="sessionTier"
                                onOpen={() => exceptionEditor.open(selectedCoachId)}
                            />
                        )}
                    </Col>
                )}
                {exceptionEditor.dialogs}
            </Row>
        );
    },
);

export default SessionTierCommissionTab;
